import { TFunction } from 'react-i18next';
import moment from 'moment';
import * as yup from 'yup';

import { getCountryCallingCodeAndNationalNumber } from 'utils/phone';
import { PersonsDocumentsType } from 'entities/dashboard';
import { calcProcentege } from 'pages/dashboard/utils';
import { OwnerTypes } from 'entities/ownership';
import {
  stringRequired,
  validateCity,
  validateEmail,
  validatePhone,
  validatePostalCode,
  validateState,
} from 'utils/validation';

import { IdTypes } from './constants';
import { CountryISOCodes } from 'common/countries';
import { ApplicationStatuses } from 'entities/progress';

const getOwnershipPercentageSchema = (t, totalShare) =>
  yup
    .number()
    .typeError(t('ownership:fields.ownershipPercentage.validation.required'))
    .min(
      0.01,
      t('ownership:fields.ownershipPercentage.validation.min', {
        value: 0,
      }),
    )
    .max(
      100 - totalShare,
      t('ownership:fields.ownershipPercentage.validation.total'),
    )
    .required(t('ownership:fields.ownershipPercentage.validation.required'))
    .test(
      'shareBelow20',
      t('ownership:fields.ownershipPercentage.validation.below20'),
      function (value) {
        return !(this.parent.isOver20 === false && (value || 0) >= 20);
      },
    )
    .test(
      'shareOver20',
      t('ownership:fields.ownershipPercentage.validation.over20'),
      function (value) {
        return !(this.parent.isOver20 === true && (value || 0) < 20);
      },
    );

const getIsOver20Schema = (t) =>
  yup
    .boolean()
    .nullable()
    .required(t('ownership:fields.shareQuestion.validation.required'));

const getDescriptionById = (
  types: PersonsDocumentsType[],
  id: number | string,
): string =>
  types.find((type) => type.personDocumentsTypesId === Number(id))
    ?.description || '';

export const getOwnersPersonSchema = (
  t: TFunction,
  totalShare: number,
  types: PersonsDocumentsType[],
) =>
  yup
    .object()
    .shape({
      isOver20: getIsOver20Schema(t),
      ownershipPercentage: getOwnershipPercentageSchema(t, totalShare),
      givenName1: stringRequired(t, 'common:fields.firstname.label'),
      surnameFirst: stringRequired(t, 'common:fields.lastname.label'),
      dateOfBirth: yup
        .date()
        .nullable()
        .when('isOver20', {
          is: true,
          then: yup
            .date()
            .typeError(t('ownership:fields.dateOfBirth.validation.valid'))
            .test(
              'dateOfBirth',
              t('ownership:fields.dateOfBirth.validation.valid'),
              (value) => {
                const years = moment().diff(moment(value), 'years');
                return years > 16 && years < 120;
              },
            )
            .required(t('ownership:fields.dateOfBirth.validation.required')),
        }),
      countryIsocode: yup
        .string()
        .nullable()
        .when('isOver20', {
          is: true,
          then: stringRequired(t, 'common:fields.country.label'),
        }),
      addressLine1: yup
        .string()
        .nullable()
        .when('isOver20', {
          is: true,
          then: stringRequired(t, 'common:fields.address.label'),
        }),
      city: yup
        .string()
        .nullable()
        .when('isOver20', {
          is: true,
          then: validateCity(t),
        }),
      postalCode: yup
        .string()
        .nullable()
        .when('isOver20', {
          is: true,
          then: validatePostalCode(t),
        }),
      stateOrProvince: yup
        .string()
        .nullable()
        .when('isOver20', {
          is: true,
          then: validateState(t),
        }),
      emailAddress: yup
        .string()
        .nullable()
        .when('isOver20', {
          is: true,
          then: validateEmail(t),
        }),
      phone: yup
        .string()
        .nullable()
        .when('isOver20', {
          is: true,
          then: validatePhone(t),
        }),
      personDocumentsTypesId: yup
        .string()
        .nullable()
        .when('isOver20', {
          is: true,
          then: stringRequired(t, 'ownership:fields.idType.label'),
        }),
      idTypeName: yup
        .string()
        .nullable()
        .when(['isOver20', 'personDocumentsTypesId'], {
          is: (isOver20, id: number) =>
            isOver20 === true &&
            getDescriptionById(types, id) === IdTypes.Other,
          then: stringRequired(t, 'ownership:fields.idTypeName.label'),
          otherwise: yup.string().nullable().notRequired(),
        }),
      idnumber: yup
        .string()
        .transform((value, originalValue) =>
          originalValue == null ? '' : value,
        )
        .trim()
        .when(['isOver20', 'personDocumentsTypesId'], {
          is: (isOver20, id: number) =>
            isOver20 === true && getDescriptionById(types, id) === IdTypes.SSN,
          then: yup
            .string()
            .required(
              t('common:validations.required', {
                name: t('ownership:fields.idnumber.label'),
              }),
            )
            .length(11, t('ownership:fields.idnumber.validation.ssn')),
          otherwise: yup.string().when(['isOver20', 'personDocumentsTypesId'], {
            is: (isOver20, id: number) =>
              isOver20 === true &&
              getDescriptionById(types, id) === IdTypes.Passport,
            then: yup
              .string()
              .required(
                t('common:validations.required', {
                  name: t('ownership:fields.idnumber.label'),
                }),
              )
              .min(6, t('ownership:fields.idnumber.validation.passport'))
              .max(12, t('ownership:fields.idnumber.validation.passport')),
            otherwise: yup.string().when('isOver20', {
              is: true,
              then: yup.string().required(
                t('common:validations.required', {
                  name: t('ownership:fields.idnumber.label'),
                }),
              ),
              otherwise: yup.string().nullable().notRequired(),
            }),
          }),
        }),
      idcountryIsocode: yup
        .string()
        .nullable()
        .when('isOver20', {
          is: true,
          then: stringRequired(t, 'ownership:fields.idIssuingCountry.label'),
        }),
    })
    .required();

export const getOwnersBusinessSchema = (t: TFunction, totalShare: number) =>
  yup
    .object()
    .shape({
      businessLegalName: stringRequired(
        t,
        'common:fields.businessLegalName.label',
      ),
      isOver20: getIsOver20Schema(t),
      ownershipPercentage: getOwnershipPercentageSchema(t, totalShare),
      addressLine1: yup
        .string()
        .nullable()
        .when('isOver20', {
          is: true,
          then: stringRequired(t, 'common:fields.address.label'),
        }),
      city: yup
        .string()
        .nullable()
        .when('isOver20', {
          is: true,
          then: validateCity(t),
        }),
      postalCode: yup
        .string()
        .nullable()
        .when('isOver20', {
          is: true,
          then: validatePostalCode(t),
        }),
      stateOrProvince: yup
        .string()
        .nullable()
        .when('isOver20', {
          is: true,
          then: validateState(t),
        }),
      countryIsocode: yup
        .string()
        .nullable()
        .when('isOver20', {
          is: true,
          then: stringRequired(t, 'common:fields.country.label'),
        }),
      emailAddress: yup
        .string()
        .trim()
        .nullable()
        .when('isOver20', {
          is: true,
          then: yup
            .string()
            .required(t('common:fields.email.validation.required'))
            .email(t('common:fields.email.validation.valid')),
        }),
      phone: yup
        .string()
        .nullable()
        .when('isOver20', {
          is: true,
          then: validatePhone(t, 'ownership:fields.businessPhone.label'),
        }),
      countryofIncorporationIsocode: yup
        .string()
        .nullable()
        .when('isOver20', {
          is: true,
          then: stringRequired(t, 'common:fields.country.label'),
        }),
    })
    .required();

export const getMappedOwner = (owner) => ({
  ...owner,
  addressLine2: owner.addressLine2 ? owner.addressLine2.trim() : '',
  addressLine3: owner.addressLine3 ? owner.addressLine3.trim() : '',
  emailAddress: owner.emailAddress ? owner.emailAddress.trim() : '',
  stateOrProvince: owner.stateOrProvince ? owner.stateOrProvince.trim() : '',
  type: owner.businessLegalName ? OwnerTypes.business : OwnerTypes.person,
  dateOfBirth: owner.dateOfBirth ? new Date(owner.dateOfBirth) : null,
  phone: `+${owner.countryCallingCode?.replace('+', '') || ''}${
    owner.telephoneNumber || owner.businessContactNumber || ''
  }`,
  alpha2Code: owner.addressAlpha2Code || 'US',
  idnumber: owner.idnumber || '',
  personDocumentsTypesId: owner.personDocumentsTypesId || '',
  idTypeName: owner.idTypeName || '',
  idcountryIsocode: owner.idcountryIsocode || '',
});

export const getMappedOwners = (owners) =>
  owners.map((owner) => getMappedOwner(owner));

export const getMappedOwnerPersonFields = ({ data, business }) => {
  const { isOver20, ...fields } = data;
  const { countryCallingCode, nationalNumber } = data.phone
    ? getCountryCallingCodeAndNationalNumber(data.phone)
    : { countryCallingCode: null, nationalNumber: null };

  return {
    ...fields,
    isActive: true,
    businessesId: business.businessesId,
    countryCallingCode: countryCallingCode,
    telephoneNumber: nationalNumber,
  };
};

export const getFormFields = (values) => {
  const fields: any = {
    ownershipPercentage: values.ownershipPercentage,
    countryIsocode: values.countryIsocode,
    addressLine1: values.addressLine1,
    city: values.city,
    postalCode: values.postalCode,
    phone: values.phone,
  };

  if (values.countryIsocode !== CountryISOCodes.UnitedKingdom) {
    fields.stateOrProvince = values.stateOrProvince;
  }

  if (!values.type || values.type === OwnerTypes.person) {
    const personFields: any = {
      ...fields,
      givenName1: values.givenName1,
      surnameFirst: values.surnameFirst,
      dateOfBirth: values.dateOfBirth,
      emailAddress: values.emailAddress,
      idfrontGuid: values.idfrontGuid,
      personDocumentsTypesId: values.personDocumentsTypesId,
      idnumber: values.idnumber,
      idcountryIsocode: values.idcountryIsocode,
    };

    return personFields;
  }

  if (values.type === OwnerTypes.business) {
    return {
      ...fields,
      businessLegalName: values.businessLegalName,
      countryofIncorporationIsocode: values.countryofIncorporationIsocode,
    };
  }

  return fields;
};

export const getMappedCreateOwnerPersonFields = ({
  data,
  person,
  business,
}) => {
  const fields = getMappedOwnerPersonFields({ data, business });

  return {
    ...fields,
    createdBy: person.emailAddress,
  };
};

export const getMappedUpdateOwnerPersonFields = ({
  data,
  person,
  business,
}) => {
  const fields = getMappedOwnerPersonFields({ data, business });

  return {
    ...fields,
    addressLine2: data.addressLine2 || ' ',
    modifiedBy: person.emailAddress,
    businessesOwnersPersonsId: data.businessesOwnersPersonsId,
  };
};

export const getMappedOwnerBusinessFields = ({ data, business }) => {
  const { countryCallingCode, nationalNumber } = data.phone
    ? getCountryCallingCodeAndNationalNumber(data.phone)
    : { countryCallingCode: null, nationalNumber: null };

  return {
    isActive: true,
    businessesId: business.businessesId,
    businessLegalName: data.businessLegalName,
    ownershipPercentage: data.ownershipPercentage,
    addressLine1: data.addressLine1,
    addressLine2: data.addressLine2,
    addressLine3: data.addressLine3,
    city: data.city,
    postalCode: data.postalCode,
    stateOrProvince: data.stateOrProvince,
    countryIsocode: data.countryIsocode,
    businessContactName: data.countryIsocode,
    emailAddress: data.emailAddress,
    countryCallingCode: countryCallingCode,
    businessContactNumber: nationalNumber,
    countryofIncorporationIsocode: data.countryofIncorporationIsocode,
    noDUNSListed: true,
  };
};

export const getMappedCreateOwnerBusinessFields = ({
  data,
  person,
  business,
}) => {
  const fields = getMappedOwnerBusinessFields({ data, business });

  return {
    createdBy: person.emailAddress,
    ...fields,
  };
};

export const getMappedUpdateOwnerBusinessFields = ({
  data,
  person,
  business,
}) => {
  const fields = getMappedOwnerBusinessFields({ data, business });

  return {
    ...fields,
    addressLine2: data.addressLine2 || ' ',
    addressLine3: data.addressLine3 || ' ',
    emailAddress: data.emailAddress || ' ',
    modifiedBy: person.emailAddress,
    businessesOwnersBusinessesId: data.businessesOwnersBusinessesId,
  };
};

export const getMappedCreateBlobReferenceFields = ({
  file,
  person,
  business,
  blobReferencesTypesId,
}) => {
  const fileName = file.name.split('.');

  const formData = new FormData();

  formData.append('isActive', 'true');
  formData.append('createdBy', person.emailAddress);
  formData.append('businessesId', business.businessesId.toString());
  formData.append('blobReferencesTypesId', blobReferencesTypesId.toString());
  formData.append('blobFileSize', file.size.toString());
  formData.append('blobFileName', file.name);
  formData.append('blobFileExtension', fileName[1]);
  formData.append('file', file);

  return formData;
};

export const getIsOver20 = (owner) =>
  owner.ownershipPercentage == null
    ? undefined
    : owner.ownershipPercentage >= 20;

export const getTotalShareBasisPoints = (owners) =>
  owners
    .filter((owner) => owner.isActive)
    .reduce(
      (total, owner) =>
        total + Math.round(parseFloat(owner.ownershipPercentage || 0) * 100),
      0,
    );

export const getOwnerProgressFields = (owner) => {
  if ((owner.ownershipPercentage || 0) >= 20) {
    return getFormFields(owner);
  }

  if (owner.type === OwnerTypes.business) {
    return {
      businessLegalName: owner.businessLegalName,
      ownershipPercentage: owner.ownershipPercentage,
    };
  }

  return {
    givenName1: owner.givenName1,
    surnameFirst: owner.surnameFirst,
    ownershipPercentage: owner.ownershipPercentage,
  };
};

export const isOwnerComplete = (owner) =>
  calcProcentege(getOwnerProgressFields(owner)).progress === 100;

export const calcOwnershipPercentage = ({ owners, hasFile }, business) => {
  const activeOwners = owners.filter((owner) => owner.isActive);

  const ownerFields = activeOwners.reduce((fields, owner, index) => {
    Object.entries(getOwnerProgressFields(owner)).forEach(([key, value]) => {
      fields[`${key}-${index}`] = value;
    });

    return fields;
  }, {});

  const result = calcProcentege({
    ...ownerFields,
    hasOwners: activeOwners.length > 0,
    totalBreakdown: getTotalShareBasisPoints(owners) === 10000,
    hasFile,
  });

  return business.applicationStatusTypeCode === ApplicationStatuses.approved &&
    result.progress < 100
    ? { ...result, showTabErrors: true }
    : result;
};

export const getMappedOwnerBlobReferences = (blobs) =>
  blobs.map((blob) => ({
    ...blob,
    id: blob.businessesOwnersBlobReferencesId,
  }));
